import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight, FaTrophy, FaStar } from "react-icons/fa";

const BASE_URL = "https://api.dreamtoday.in";

// Generate consistent avatar emoji from name
const getAvatar = (name) => {
  const emojis = ["🏆", "🎉", "🌟", "🎊", "👑", "💎", "🥇", "🎁"];
  if (!name || typeof name !== "string" || name.trim() === "") return "🎁";
  const index = name.trim().charCodeAt(0) % emojis.length;
  return emojis[index];
};

// Robust date formatter for "2 Apr 2026, 12:24:20 pm" format
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  // Parse custom format: "2 Apr 2026, 12:24:20 pm"
  const parsed = new Date(dateString.replace(/(\d{1,2})\s(\w{3})\s(\d{4}),\s(.+)/, "$2 $1, $3 $4"));
  if (isNaN(parsed.getTime())) return dateString; // fallback to original if parsing fails
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const filterValidWinners = (bookings) => {
  return bookings.filter((b) => {
    const hasUser = b.userName && b.userName.trim() !== "";
    const hasPrize = b.winningGift && b.winningGift.trim() !== "";

    return hasUser && hasPrize;
  });
};

const LiveWinners = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/admin/winning-gifts`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && Array.isArray(data.bookings)) {
          const validWinners = filterValidWinners(data.bookings);
          setWinners(validWinners);
        } else {
          console.warn("API returned invalid data structure");
          setWinners([]);
        }
      } catch (err) {
        console.error("Failed to fetch winners:", err);
        setError(err.message);
        setWinners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWinners();
  }, []);

  // Auto-rotate slider
  useEffect(() => {
    if (winners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % winners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [winners]);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % winners.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + winners.length) % winners.length);

  // Hide section if loading done and no valid winners
  if (!loading && winners.length === 0) {
    if (error) {
      return (
        <section className="py-24 bg-gradient-to-b from-white via-[#FFF7DB] to-white">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-red-500">Unable to load winners. Please try again later.</p>
          </div>
        </section>
      );
    }
    return null;
  }

  const winner = winners[currentIndex];

  return (
    <section className="py-24 bg-gradient-to-b from-white via-[#FFF7DB] to-white relative overflow-hidden">
      {/* Soft gold glow background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(250,204,21,0.2),transparent_70%)]" />

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold mb-3">
            <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
              Live Winners
            </span>
          </h2>
          <p className="text-gray-600">Real winners turning dreams into reality</p>
          {winners.length > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Showing {currentIndex + 1} of {winners.length} winners
            </p>
          )}
        </div>

        {/* Skeleton Loader */}
        {loading && (
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-[#F5D77A] shadow-xl animate-pulse">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-yellow-200 shrink-0" />
              <div className="flex-1 w-full space-y-4">
                <div className="h-7 bg-yellow-100 rounded w-1/2 mx-auto md:mx-0" />
                <div className="h-6 bg-yellow-100 rounded w-2/3 mx-auto md:mx-0" />
                <div className="h-4 bg-yellow-50 rounded w-1/3 mx-auto md:mx-0" />
                <div className="h-4 bg-yellow-50 rounded w-full" />
                <div className="h-4 bg-yellow-50 rounded w-4/5" />
              </div>
            </div>
            <div className="mt-10 w-full bg-gray-100 h-1 rounded-full" />
          </div>
        )}

        {/* Winner Slider */}
        {!loading && winner && (
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={winner.bookingId || currentIndex}
                initial={{ opacity: 0, scale: 0.95, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -40 }}
                transition={{ duration: 0.6 }}
                className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-[#F5D77A] shadow-xl"
              >
                <div className="flex flex-col md:flex-row items-center gap-10">
                  {/* Avatar with Trophy Badge */}
                  <div className="relative shrink-0">
                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-r from-[#FFE9A8] to-[#FFD86B] flex items-center justify-center text-6xl shadow-md">
                      {getAvatar(winner.userName)}
                    </div>
                    <div className="absolute -bottom-3 -right-3 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full p-3 shadow-md">
                      <FaTrophy className="text-white text-xl" />
                    </div>
                  </div>

                  {/* Winner Details */}
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      {winner.userName}
                    </h3>

                    <p className="text-2xl mb-2 bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent font-semibold">
                      Won {winner.winningGift}
                    </p>

                    <p className="text-sm text-yellow-700 font-medium mb-1">
                      {winner.campaignTitle}
                    </p>
                    <p className="text-gray-500 text-sm mb-3">
                      {formatDate(winner.bookingDate)}
                    </p>

                    {/* Star Rating */}
                    {winner.rating > 0 && (
                      <div className="flex gap-1 justify-center md:justify-start mb-4" aria-label={`Rating: ${winner.rating} out of 5 stars`}>
                        {[...Array(5)].map((_, i) => (
                          <FaStar
                            key={i}
                            className={i < winner.rating ? "text-yellow-500" : "text-yellow-200"}
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                    )}

                    {/* Feedback Testimonial */}
                    {winner.feedback && winner.feedback.trim() !== "" && (
                      <p className="text-lg italic text-gray-700 border-l-4 border-yellow-400 pl-4 py-2 bg-yellow-50 rounded-r">
                        "{winner.feedback}"
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons (Desktop Only) */}
            {winners.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-xl p-4 rounded-full border border-[#F5D77A] shadow-md hidden md:block hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  aria-label="Previous winner"
                >
                  <FaChevronLeft className="text-yellow-600" />
                </button>

                <button
                  onClick={nextSlide}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 bg-white/80 backdrop-blur-xl p-4 rounded-full border border-[#F5D77A] shadow-md hidden md:block hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  aria-label="Next winner"
                >
                  <FaChevronRight className="text-yellow-600" />
                </button>
              </>
            )}

            {/* Progress Bar */}
            <div className="mt-10 w-full bg-gray-200 h-1 rounded-full overflow-hidden" aria-hidden="true">
              <motion.div
                key={currentIndex}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 6, ease: "linear" }}
                className="h-full bg-gradient-to-r from-yellow-500 to-yellow-600"
              />
            </div>

            {/* Pagination Dots */}
            {winners.length > 1 && (
              <div className="flex justify-center gap-2 mt-5" role="tablist" aria-label="Winner slides">
                {winners.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 ${i === currentIndex
                      ? "w-6 h-2 bg-yellow-500"
                      : "w-2 h-2 bg-yellow-200 hover:bg-yellow-300"
                      }`}
                    role="tab"
                    aria-selected={i === currentIndex}
                    aria-label={`Go to winner ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default LiveWinners;